// Orders + checkout API (Phase 4). Cart stock, COD checkout, admin order management.
import { api, apiBlob } from './client';

export type OrderListFilters = {
  from?: string; // ISO UTC
  to?: string;   // ISO UTC
  status?: string;
  q?: string;
};

function toQuery(filters: OrderListFilters = {}): string {
  const p = new URLSearchParams();
  if (filters.from) p.set('from', filters.from);
  if (filters.to) p.set('to', filters.to);
  if (filters.status && filters.status !== 'all') p.set('status', filters.status);
  if (filters.q) p.set('q', filters.q);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export type ShippingArea = {
  id: string;
  name_ar: string;
  name_en?: string | null;
  shipping_price: number;
  delivery_time: string;
  delivery_days: number;
};

export type StockRow = { color_id: string; size: string; stock_qty: number };

export type PlaceOrderItem = {
  product_id: string;
  color_id?: string | null;
  size?: string | null;
  quantity: number;
};

export type PlaceOrderBody = {
  customer_name: string;
  phone: string;
  address: string;
  shipping_area_id: string;
  lang: 'en' | 'ar';
  items: PlaceOrderItem[];
};

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'returned'
  | 'cancelled';

export const ordersApi = {
  // Public: active shipping areas
  shippingAreas(): Promise<{ items: ShippingArea[] }> {
    return api('/catalog/shipping-areas');
  },
  // Public: stock for cart variants
  stock(colorIds: string[]): Promise<{ items: StockRow[] }> {
    const ids = colorIds.filter(Boolean).join(',');
    return api(`/catalog/stock?colorIds=${encodeURIComponent(ids)}`);
  },
  // Authenticated: place a COD order — server computes money + validates stock
  place(body: PlaceOrderBody): Promise<{ id: string; order_number: string }> {
    return api('/catalog/orders', { method: 'POST', body, auth: true });
  },

  // Admin: list all orders (enriched items, optional server-side filters)
  adminList(filters?: OrderListFilters): Promise<{ items: any[] }> {
    return api(`/admin/orders${toQuery(filters)}`, { auth: true });
  },
  // Admin: update order status
  updateStatus(id: string, status: OrderStatus): Promise<{ ok: true; invoice_url: string | null }> {
    return api(`/admin/orders/${id}/status`, { method: 'PUT', body: { status }, auth: true });
  },
  // Admin: update order details and discount EGP amount / payment status
  update(id: string, body: { customer_name?: string; phone?: string; address?: string; city?: string; discount_amount?: number; payment_status?: string }): Promise<{ ok: true }> {
    return api(`/admin/orders/${id}`, { method: 'PUT', body, auth: true });
  },
  // Admin: (re)generate invoice PDF
  generateInvoice(id: string): Promise<{ url: string }> {
    return api(`/admin/orders/${id}/invoice`, { method: 'POST', auth: true });
  },
  // Admin: direct PDF download (returns a Blob)
  downloadInvoice(id: string): Promise<Blob> {
    return apiBlob(`/admin/orders/${id}/invoice/file`);
  },
  // Admin: bulk ZIP of invoices matching active filters (returns a Blob)
  exportInvoices(filters?: OrderListFilters): Promise<Blob> {
    return apiBlob(`/admin/orders/export${toQuery(filters)}`);
  },
  // Admin: delete all orders
  deleteAll(): Promise<{ ok: true }> {
    return api('/admin/orders', { method: 'DELETE', auth: true });
  },
};
